import React, { useState, useContext, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Forward, Reply, Trash2 } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import ForwardMessageModal from "../ForwardMessageModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

// Mantém a API antiga (anchorEl vindo do MessagesList) sem depender do MUI:
// o menu é posicionado a partir do retângulo do elemento âncora.
const REACOES_RAPIDAS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const MessageOptionsMenu = ({ message, menuOpen, handleClose, anchorEl }) => {
	const { setReplyingMessage } = useContext(ReplyMessageContext);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [encaminhar, setEncaminhar] = useState(false);
	const menuRef = useRef(null);

	const reagir = async (emoji) => {
		handleClose();
		try {
			await api.post(`/messages/${message.id}/reaction`, { emoji });
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(() => {
		if (!menuOpen) return undefined;

		const aoClicarFora = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				handleClose();
			}
		};
		const aoTeclar = (e) => e.key === "Escape" && handleClose();

		document.addEventListener("mousedown", aoClicarFora);
		document.addEventListener("keydown", aoTeclar);

		return () => {
			document.removeEventListener("mousedown", aoClicarFora);
			document.removeEventListener("keydown", aoTeclar);
		};
	}, [menuOpen, handleClose]);

	const apagarMensagem = async () => {
		try {
			await api.delete(`/messages/${message.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const responder = () => {
		setReplyingMessage(message);
		handleClose();
	};

	const abrirConfirmacao = () => {
		setConfirmationOpen(true);
		handleClose();
	};

	const rect = anchorEl?.getBoundingClientRect();

	return (
		<>
			<ForwardMessageModal
				open={encaminhar}
				onClose={() => setEncaminhar(false)}
				message={message}
			/>
			<ConfirmationModal
				title={i18n.t("messageOptionsMenu.confirmationModal.title")}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={apagarMensagem}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>

			{menuOpen &&
				rect &&
				createPortal(
					<div
						ref={menuRef}
						style={{
							position: "fixed",
							top: rect.bottom + 4,
							left: Math.max(8, rect.right - 265),
						}}
						className="z-50 flex flex-col items-end gap-1.5"
					>
						{/* Barra de Reação Rápida (Pill arredondado estilo WhatsApp Web) */}
						{!message.isDeleted && (
							<div className="flex items-center gap-1 rounded-full border bg-popover px-2 py-1 text-popover-foreground shadow-lg">
								{REACOES_RAPIDAS.map((emoji) => (
									<button
										key={emoji}
										type="button"
										onClick={() => reagir(emoji)}
										className="flex h-8 w-8 items-center justify-center rounded-full text-lg hover:bg-accent transition-transform duration-150 hover:scale-125"
										title={`Reagir com ${emoji}`}
									>
										{emoji}
									</button>
								))}
							</div>
						)}

						{/* Menu de Ações da Mensagem */}
						<div
							role="menu"
							className="w-48 overflow-hidden rounded-xl border bg-popover p-1 text-popover-foreground shadow-md"
						>
							<button
								type="button"
								role="menuitem"
								onClick={responder}
								className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm hover:bg-accent"
							>
								<Reply className="h-4 w-4" />
								{i18n.t("messageOptionsMenu.reply")}
							</button>

							<button
								type="button"
								role="menuitem"
								onClick={() => {
									setEncaminhar(true);
									handleClose();
								}}
								className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm hover:bg-accent"
							>
								<Forward className="h-4 w-4" />
								Encaminhar
							</button>

							{message.fromMe && (
								<button
									type="button"
									role="menuitem"
									onClick={abrirConfirmacao}
									className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-destructive hover:bg-accent"
								>
									<Trash2 className="h-4 w-4" />
									{i18n.t("messageOptionsMenu.delete")}
								</button>
							)}
						</div>
					</div>,
					document.body
				)}
		</>
	);
};

export default MessageOptionsMenu;
